<template>
  <div>
    <div class="d-flex flex-column">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltip }">
          <v-hover v-slot="{ hover }">
            <v-btn
              block
              height="80"
              class="success mr-1"
              :elevation="hover ? 8 : 2"
              v-on="{ ...tooltip }"
              :disabled="false"
              @click="openDialog"
            >
              <div class="d-flex flex-column align-center">
                <v-icon>mdi-account-arrow-right-outline</v-icon>
                <span class="action-text">COMPLETE INVESTIGATION</span>
              </div>
            </v-btn>
          </v-hover>
        </template>
        <span>{{ tooltipMessage }}</span>
      </v-tooltip>
    </div>

    <!-- Complete Investigation Dialog -->
    <v-dialog v-model="dialogCompleteInv" max-width="600px" persistent>
      <template v-if="editedCasefile">
        <v-card>
          <v-card-title class="justify-center text-h5 grey lighten-2">
            <span class="text-h5">Complete Investigation & Submit Claims</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="claimForm" lazy-validation>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <div class="mb-2 font-weight-bold">
                      Please enter your claim amounts (MYR). Use
                      <strong>0.00</strong> if not applicable.
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    v-for="field in claimFields"
                    :key="field.key"
                  >
                    <v-text-field
                      v-model.number="claim[field.key]"
                      :label="field.label"
                      type="number"
                      min="0"
                      step="0.01"
                      :prefix="'MYR'"
                      :rules="[
                        (v) =>
                          (v !== null && v !== '' && !isNaN(v) && v >= 0) ||
                          'Enter a valid amount',
                      ]"
                      :value="formatAmount(claim[field.key])"
                      persistent-hint
                      dense
                      :disabled="isSubmitting"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-divider class="my-2"></v-divider>
                  </v-col>
                  <v-col cols="12">
                    <v-btn
                      tile
                      dark
                      small
                      @click="closeCompleteInvDialog"
                      class="mt-3 mr-2 error"
                      :disabled="isSubmitting"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      tile
                      small
                      :disabled="!isFormValid || isSubmitting"
                      class="mt-3 success"
                      @click="submitClaims"
                    >
                      Submit
                    </v-btn>
                  </v-col>
                </v-row>
                <v-overlay :value="isSubmitting" opacity="0.7">
                  <div class="d-flex flex-column align-center justify-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="40"
                    />
                    <div class="mt-3 font-weight-bold">
                      Submitting, please wait...
                    </div>
                  </div>
                </v-overlay>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </template>
    </v-dialog>

    <!-- Confirm 0.00 Claims Dialog -->
    <v-dialog v-model="confirmZeroDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Submission</v-card-title>
        <v-card-text>
          You are submitting <strong>0.00</strong> for all claim categories.<br />
          Are you sure you want to proceed?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="confirmZeroDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" text @click="submitClaims(true)"
            >Yes, Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CasefileDataService from "../../services/CasefileDataService";
import ClaimsV3DataService from "../../services/ClaimsV3DataService";
import MailDataService from "../../services/MailDataService";
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "compInvestigation",
  props: [
    "claims",
    "branches",
    "insurer",
    "refType",
    "subRefTypes",
    "clerks",
    "members",
    "documents",
  ],
  data() {
    return {
      dialogCompleteInv: false,
      confirmZeroDialog: false,
      editedCasefile: null,
      isSubmitting: false, // <-- loading state
      claim: {
        travMileage: 0.0,
        additional: 0.0,
        sdAndStamps: 0.0,
        medicalReport: 0.0,
        policeDoc: 0.0,
        jpjDoc: 0.0,
        misc: 0.0,
        typing: 0.0,
      },
      claimFields: [
        { key: "travMileage", label: "Travelling / Mileage" },
        { key: "additional", label: "Additional" },
        { key: "sdAndStamps", label: "SD & Stamps" },
        { key: "medicalReport", label: "Medical Report" },
        { key: "policeDoc", label: "Police Document" },
        { key: "jpjDoc", label: "JPJ Document" },
        { key: "misc", label: "Miscellaneous" },
        { key: "typing", label: "Typing" },
      ],
    };
  },
  computed: {
    ...mapState(["caseState"]),
    tooltipMessage() {
      if (this.caseState.casefile.fileStatus !== "INVE") {
        return "Case is not in investigation stage";
      }
      if (this.caseState.casefile.adjuster !== this.$store.state.auth.user.id) {
        return "You are not assigned as the adjuster";
      }
      return "COMPLETE INVESTIGATION";
    },
    isFormValid() {
      // All fields must be numbers >= 0
      return this.claimFields.every(
        (f) =>
          this.claim[f.key] !== null &&
          this.claim[f.key] !== "" &&
          !isNaN(this.claim[f.key]) &&
          Number(this.claim[f.key]) >= 0
      );
    },
    allClaimsZero() {
      return this.claimFields.every((f) => Number(this.claim[f.key]) === 0);
    },
  },
  methods: {
    formatAmount(val) {
      return Number(val).toFixed(2);
    },
    openDialog() {
      // Clone casefile for local editing
      this.editedCasefile = JSON.parse(JSON.stringify(this.caseState.casefile));
      // Reset claims to 0.00
      this.claimFields.forEach((f) => {
        this.claim[f.key] = 0.0;
      });
      this.dialogCompleteInv = true;
    },
    closeCompleteInvDialog() {
      this.dialogCompleteInv = false;
      this.editedCasefile = null;
    },
    submitClaims(force = false) {
      if (!this.editedCasefile) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: "Casefile data is missing. Please try again.",
        });
        return;
      }
      if (this.allClaimsZero && !force) {
        this.confirmZeroDialog = true;
        return;
      }
      this.isSubmitting = true; // show overlay
      const payload = {
        ...this.editedCasefile,
        fileStatus: "PEDI",
        dateEndInv: moment().format(),
      };
      CasefileDataService.update(this.editedCasefile.id, payload)
        .then((response) => {
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "warning",
            text:
              (response && response.data && response.data.message) ||
              toString(),
            timeout: 2000,
          });
        })
        .catch((e) => {
          this.$store.dispatch("setSnackbar", {
            showing: true,
            text:
              (e.response && e.response.data && e.response.data.message) ||
              e.message ||
              toString(),
          });
        })
        .then(() => {
          var data = {
            caseId: this.editedCasefile.id,
            adjId: this.$store.state.auth.user.id,
            travMileage: this.formatAmount(this.claim.travMileage),
            additional: this.formatAmount(this.claim.additional),
            sdAndStamps: this.formatAmount(this.claim.sdAndStamps),
            medicalReport: this.formatAmount(this.claim.medicalReport),
            policeDoc: this.formatAmount(this.claim.policeDoc),
            jpjDoc: this.formatAmount(this.claim.jpjDoc),
            misc: this.formatAmount(this.claim.misc),
            typing: this.formatAmount(this.claim.typing),
          };
          // Create claim via V3 API (handles lifecycle tracking, duplicate detection)
          ClaimsV3DataService.create(data)
            .then((response) => {
              this.claims.id = response.data.id;
              this.$store.dispatch("setSnackbar", {
                showing: true,
                color: "success",
                text: "Claim submitted successfully",
                timeout: 4000,
              });
            })
            .catch((e) => {
              this.$store.dispatch("setSnackbar", {
                showing: true,
                text: (e.response?.data?.message) || e.message || "Claim submission failed",
              });
            })
            .then(() => {
              const assignedBranch = this.caseState.casefile.branch;
              const branchManager = this.filterManagerkByBranch(
                this.findBrcode(assignedBranch)
              );
              const assignedDept = this.caseState.casefile.refType;
              const personInCharge = this.findUserEmail(
                this.filterPicID(assignedDept)
              );

              //  recipient: this.findUserEmail(this.filterPicID(assignedDept)),

              var dataEmail = {
                id: this.caseState.casefile.id,
                refType: this.filterDept(this.caseState.casefile.refType)[0]
                  .description,
                refTypeCode: this.filterDept(this.caseState.casefile.refType)[0]
                  .name,
                subRefType: this.filterSubDept(
                  this.caseState.casefile.subRefType
                )[0].subCodeDesc,
                subRefCode: this.filterSubDept(
                  this.caseState.casefile.subRefType
                )[0].subCode,
                branch: this.filterBranchName(this.caseState.casefile.branch),
                branchCode: this.filterBranches(
                  this.caseState.casefile.branch
                )[0].brCode,
                insurer: this.filterIns(this.caseState.casefile.insurer)[0]
                  .name,
                vehicleNo: this.caseState.casefile.vehicleNo
                  .toLowerCase()
                  .replace(/\s/g, ""),
                recipient: branchManager + "," + personInCharge,

                createdBy: this.$store.state.auth.user.id,
              };
              MailDataService.assignEditor(dataEmail)

                .then((response) => {
                  this.$store.dispatch("setSnackbar", {
                    showing: true,
                    color: "warning",
                    text:
                      (response && response.data && response.data.message) ||
                      toString(),
                    timeout: 6000,
                  });
                })
                .catch((e) => {
                  this.$store.dispatch("setSnackbar", {
                    showing: true,
                    text:
                      (e.response &&
                        e.response.data &&
                        e.response.data.message) ||
                      e.message ||
                      toString(),
                  });
                })
                .then(() => {
                  this.confirmZeroDialog = false;
                  this.isSubmitting = false;
                  this.closeCompleteInvDialog();
                  // Notify parent to refresh casefile immediately
                  this.$emit("refresh-casefile");
                })
                .catch((e) => {
                  console.log(e);
                  this.confirmZeroDialog = false;
                  this.isSubmitting = false;
                  this.closeCompleteInvDialog();
                });
            })
            .catch((e) => {
              console.log(e);
              this.confirmZeroDialog = false;
              this.isSubmitting = false;
              this.closeCompleteInvDialog();
            });
        });
    },
    filterBranchName(item) {
      if (!item) return "TBA";
      const filteredbranch = this.branches.filter(
        (branch) => branch.id === item
      );
      return filteredbranch[0].name;
    },
    filterBranches(item) {
      return this.branches.filter((branch) => branch.id === item);
    },

    findBrcode(item) {
      if (!item) return "TBA";
      const filteredbranch = this.branches.filter(
        (branch) => branch.id === item
      );
      return filteredbranch.map((b) => b.brCode);
    },

    filterDept(item) {
      return this.refType.filter((dept) => dept.id === item);
    },
    filterSubDept(item) {
      return this.subRefTypes.filter((subRef) => subRef.id === item);
    },
    filterIns(item) {
      return this.insurer.filter((insurer) => insurer.id === item);
    },
    filterManagerkByBranch(item) {
      const filter1 = this.clerks.filter(
        (a) =>
          a.branches.some((b) => b.brCode == item) &&
          a.roles.some(
            (c) => c.roleCode === "manager" || c.roleCode === "branchmanager"
          ) &&
          a.active
      );
      const email = filter1.map((d) => d.email);
      return email;
    },

    filterPicID(item) {
      if (!item) return "TBA";
      const filteredDept = this.refType.filter((dept) => dept.id === item);
      return filteredDept[0].picID;
    },
    findUserEmail(item) {
      const user = this.members.filter(
        (member) => member.id === item && member.active
      );
      return user[0] ? user[0].email : "sys.admin@associatedadjusters.com";
    },

    moment: function (date) {
      return moment(date);
    },

    filterHandler(item) {
      return this.handlers.filter((handler) => handler.id === item);
    },
  },
};
</script>

<style scoped>
.action-text {
  font-size: 0.9rem;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
}
.v-btn.v-btn--disabled {
  opacity: 0.8 !important;
}
.refresh-button {
  font-size: 0.75rem;
}
</style>
